// @ts-nocheck
import React, { useEffect } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import { alpha, useTheme } from '@mui/material/styles';
import type { ControllerRef, FullscreenRef, Slide, SlideImage } from 'yet-another-react-lightbox';
import {
  Fullscreen,
  Thumbnails,
  Counter,
  Download,
  Zoom,
} from 'yet-another-react-lightbox/plugins';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import ImageZoomPan from './ImageZoom';

interface ReusableLightboxProps {
  slides: SlideImage[];
  isOpen: boolean;
  onClose: () => void;
  initialIndex?: number;
}

const ReusableLightbox: React.FC<ReusableLightboxProps> = ({
  slides,
  isOpen,
  onClose,
  initialIndex = 0,
}) => {
  const theme = useTheme();
  const fullscreenRef = React.useRef<FullscreenRef>(null);
  const ref = React.useRef<ControllerRef>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // Check if we should show navigation (only if more than 1 slide)
  const hasSingleElement = slides.length > 1;

  const getContainerStyles = () => {
    return {
      backgroundColor: theme.palette.background.paper,
      width: isFullscreen ? 'auto' : '80vw',
      height: isFullscreen ? 'auto' : '80vh',
      margin: 'auto',
      borderRadius: theme.shape.borderRadius,
    };
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const container = document.querySelector('.yarl__container');
      const root = document.querySelector('.yarl__root');
      const thumbnails = document.querySelector('.yarl__thumbnails_container');
      if (
        container &&
        root &&
        thumbnails &&
        !container.contains(event.target as Node) &&
        !thumbnails.contains(event.target as Node) &&
        root.contains(event.target as Node)
      ) {
        ref.current.close();
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleFocus = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const sliderContainer = document.querySelector('.yarl__container');
      const activeButton = document.activeElement as HTMLElement;

      if (activeButton?.tagName === 'BUTTON' && target.tagName !== 'BUTTON') {
        (sliderContainer as HTMLElement).focus();
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleFocus);
    } else {
      document.removeEventListener('click', handleFocus);
    }

    return () => {
      document.removeEventListener('click', handleFocus);
    };
  }, [isOpen]);

  return (
    <Lightbox
      plugins={[Fullscreen, ...(hasSingleElement ? [Thumbnails] : []), Counter, Download]}
      open={isOpen}
      close={onClose}
      slides={slides}
      index={initialIndex}
      on={{
        enterFullscreen: () => setIsFullscreen(true),
        exitFullscreen: () => setIsFullscreen(false),
      }}
      styles={{
        container: getContainerStyles(),
        root: {
          borderColor: theme.palette.divider,
          borderWidth: '1px',
          backgroundColor: alpha(theme.palette.background.default, 0.92),
          textShadow: 'none',
          filter: 'none',
          '--yarl__button_filter': 'none',
          '--yarl__counter_filter': 'none',
        },
        button: {
          color: theme.palette.text.secondary,
          backgroundColor: 'transparent',
          borderRadius: 0,
          boxShadow: 'none',
          textShadow: 'none',
          filter: 'none',
        },
        // Hide navigation buttons when only one slide
        navigationPrev: hasSingleElement
          ? {}
          : {
              display: 'none',
            },
        navigationNext: hasSingleElement
          ? {}
          : {
              display: 'none',
            },
      }}
      counter={{
        container: {
          style: {
            color: theme.palette.text.secondary,
            backgroundColor: 'transparent',
            borderRadius: 0,
            boxShadow: 'none',
            textShadow: 'none',
          },
        },
      }}
      fullscreen={{
        ref: fullscreenRef,
      }}
      render={{
        slide: ({ slide }: { slide: Slide }) => {
          if ('src' in slide) {
            return <ImageZoomPan slide={slide} />;
          }

          if ('video' in slide) {
            return null;
          }

          return null;
        },
      }}
      toolbar={{
        buttons: ['fullscreen', 'close'],
      }}
      controller={{ ref }}
      carousel={{
        finite: slides.length === 1, // Prevents infinite looping when only one slide
        preload: hasSingleElement ? 2 : 0,
      }}
    />
  );
};

export default ReusableLightbox;
